import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "context/AuthContext";
import useBodyScrollLock from "hooks/useBodyScrollLock";
import { callAddRole } from "api/MuyeonApi";
import { MEMBER_TYPES, MEMBER_TYPE_EMOJI } from "constants/memberTypes";
import { landingSignupRole, safeRedirect } from "util/pendingPosting";

/**
 * 웹(브라우저) 회원가입 직후 회원유형 선택 온보딩.
 *  - 네이티브는 openRoleOnboarding()(넷플릭스식 풀커버) 을 쓰지만, 웹은 진입 수단이 없어(구 /memberSwitch 폐기)
 *    가입 직후 유형선택 공백이 생긴다 → 이 웹 전용 폴백 모달로 메운다.
 *  - 네이티브 __onRoleComplete(AppRouter.js) 와 동일 흐름: callAddRole → refreshRoles → 유형별 랜딩.
 *  - 일반회원 외 유형은 인증 화면으로 바로 연결하고, 승인 전에는 활동 유형으로 부여하지 않는다.
 *
 * CSS: 새 클래스 없이 후니드 기존 모달(modal-bottom-dimmed) + btn-group flex-col + btn-md 만 사용.
 */

// 회원유형별 온보딩 후 랜딩 — AppRouter.js 의 ROLE_LANDING 과 반드시 동일하게 유지.
const ROLE_LANDING = {
  HOBBY: "/lessons",
  ACADEMY: "/jobs",
  TEAM: "/jobs",
  TEACHER: "/jobs",
  DANCER: "/jobs",
};
// 서류 인증(관리자 승인)이 필요한 회원유형 — AppRouter.js 와 동일.
//  ★ 2026-10-12 강사·무용수는 본인 인증(가입 때 완료)으로 바로 부여되어 빠졌다.
const VERIFICATION_REQUIRED_ROLES = ["ACADEMY", "TEAM"];

export default function WebRoleOnboarding({ isShow, redirect = "/home" }) {
  const navigate = useNavigate();
  const { refreshRoles } = useAuth();
  const [busy, setBusy] = useState(false);
  useBodyScrollLock(isShow);

  const decoded = (() => {
    try { return decodeURIComponent(redirect); } catch (e) { return redirect; }
  })();

  // 유형별 랜딩(활동유형은 refreshRoles 후 AuthContext 가 자동 선택 — 네이티브 __onRoleComplete 와 동일).
  // 레슨 요청을 쓰다 가입한 경우는 고른 유형과 상관없이 요청 화면으로 돌아간다(10-24 광고 이탈 방지).
  //  ★ 2026-11-03 공유된 견적요청(/availableQuotes/:id)에서 가입한 경우도 그 요청으로 돌아가 바로 견적을 보낸다.
  const backToTask = (decoded || "").startsWith("/quoteRequest") || (decoded || "").startsWith("/availableQuotes/");
  const land = (role) => navigate(backToTask ? decoded : ROLE_LANDING[role] || decoded || "/home", { replace: true });
  // 건너뛰기 → 원래 목적지(기본 홈).
  const skip = () => navigate(decoded || "/home", { replace: true });

  const pick = async (role) => {
    if (busy) return;
    setBusy(true);
    // 실패해도 진행(앱에서 재시도 가능) — 네이티브 __onRoleComplete 도 addRole 실패를 무시한다.
    try { await callAddRole(role); } catch (e) { /* noop */ }
    try { await refreshRoles(); } catch (e) { /* noop */ }
    setBusy(false);
    if (VERIFICATION_REQUIRED_ROLES.includes(role)) {
      // 원래 목적지(예: 광고로 쓰던 공고 → /subs/create?resume=pending)를 서류 제출 화면에 넘긴다.
      //  제출이 끝나면 MemberTypeManage 가 그 경로로 돌려보낸다(홈이면 생략 — 종전 동작).
      const back = safeRedirect(redirect);
      const q = back && back !== "/home" ? `&redirect=${encodeURIComponent(back)}` : "";
      navigate(`/my/memberType?verify=${encodeURIComponent(role)}${q}`, { replace: true });
      return;
    }
    land(role);
  };

  // 광고 유입(?lp=teacher_sub → 강사, academy_job → 학원)이면 그 유형을 맨 위에 강조해 보여 준다.
  //  견적요청 공유 링크로 가입하면 견적을 보낼 수 있는 강사를 맨 위에 강조한다.
  const suggested = landingSignupRole() || ((decoded || "").startsWith("/availableQuotes/") ? "TEACHER" : null);
  const types = suggested
    ? [...MEMBER_TYPES.filter((t) => t.code === suggested), ...MEMBER_TYPES.filter((t) => t.code !== suggested)]
    : MEMBER_TYPES;

  return (
    <div data-id="web-role-onboarding" className={"modal-bottom-dimmed" + (isShow ? " is-show" : "")}>
      <div className="modal-bottom">
        <>
            <div className="tit-box">
              <p>어떤 활동으로 시작할까요?</p>
              <button type="button" className="ico-btn" onClick={skip}>
                <i className="close" />
                <span className="visually-hidden">건너뛰기</span>
              </button>
            </div>
            <div className="con-box">
              <p className="t-side">나중에 마이페이지에서 언제든 추가·변경할 수 있어요.</p>
            </div>
            <div className="btn-group flex-col">
              {types.map((t) => (
                <button
                  key={t.code}
                  type="button"
                  className={t.code === suggested ? "btn-md filled primary" : "btn-md outlined"}
                  disabled={busy}
                  onClick={() => pick(t.code)}
                >
                  <span>{MEMBER_TYPE_EMOJI[t.code]} {t.label}</span>
                  {VERIFICATION_REQUIRED_ROLES.includes(t.code) && (
                    <span className="web-role-lock" aria-label="인증 필요"
                      style={t.code === suggested ? { color: "#fff" } : undefined}>잠금 · 인증 필요</span>
                  )}
                </button>
              ))}
            </div>
        </>
      </div>
    </div>
  );
}
